import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import Loader from './common/Loader';
import { getTerminology } from '../utils/terminology';

const Dashboard = () => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user?.role || user?.data?.role;
  const isStaff = userRole === 'staff';

  // Get user from localStorage or Context
  const terminology = React.useMemo(() => {
    return getTerminology(settings.school_type);
  }, [settings.school_type]);

  const studentTerm = terminology.student;
  const studentsTerm = terminology.students;
  const courseTerm = terminology.course;
  const batchTerm = terminology.batch;

  useEffect(() => {
    if (user && user.role === 'super_admin') {
      navigate('/super-admin', { replace: true });
    }
  }, [user, navigate]);

  const [stats, setStats] = useState({
    students: { total: 0, active: 0, inactive: 0 },
    finance: { today_collection: 0, monthly_expense: 0 },
    enquiries: { new_today: 0, converted_total: 0 },
    alerts: { pending_fees: 0 }
  });
  const [recentStudents, setRecentStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  const revenueData = useMemo(() => {
    return Array.isArray(stats.finance?.revenue_trends) ? stats.finance.revenue_trends : [];
  }, [stats.finance?.revenue_trends]);

  const studentStatusData = useMemo(() => {
    return [
      { name: 'Active', value: stats.students?.active || 0 },
      { name: 'Inactive', value: stats.students?.inactive || 0 },
      { name: 'Graduated', value: stats.students?.graduated || 0 },
    ].filter(item => item.value > 0);
  }, [stats.students]);

  const COLORS = ['#4361ee', '#ff9f43', '#28c76f', '#ea5455'];

  const getCurrencySymbol = (code) => {
    switch (code) {
      case 'PKR': return 'Rs. ';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'INR': return '₹';
      case 'AED': return 'AED ';
      case 'USD': default: return '$';
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (user && user.role === 'super_admin') return;

      try {
        const statsRes = await api.get('/dashboard/stats');
        setStats(statsRes.data);

        const studentsRes = await api.get('/students');

        let validStudents = [];
        if (Array.isArray(studentsRes.data)) {
          validStudents = studentsRes.data;
        } else if (studentsRes.data && Array.isArray(studentsRes.data.data)) {
          validStudents = studentsRes.data.data;
        }
        setRecentStudents(validStudents.slice(0, 5));

        setLoading(false);
      } catch (error) {
        console.error("Error fetching dashboard data", error);
        setStats({
          students: { total: 0, active: 0, inactive: 0, graduated: 0 },
          finance: { today_collection: 0, monthly_expense: 0, revenue_trends: [] },
          enquiries: { new_today: 0, converted_total: 0 },
          alerts: { pending_fees: 0 }
        });
        setRecentStudents([]);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <Loader message="Loading Dashboard..." />;

  return (
    <div className="dashboard container-fluid p-0">
      {/* Dashboard Welcome Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-4 rounded-4 shadow-sm">
        <div>
          <h2 className="fw-bold text-dark mb-1 d-flex flex-wrap align-items-center gap-3">
            Welcome back, {user.first_name}!
            {(settings.academic_session || settings.academic_year) && (
              <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-1.5 rounded-pill fw-semibold" style={{ fontSize: '0.75rem' }}>
                <i className="bi bi-calendar-event me-1"></i>
                Session: {settings.academic_session || settings.academic_year}
              </span>
            )}
          </h2>
          <p className="text-muted mb-0">Here's what's happening with your school today.</p>
        </div>
        <a 
          href="https://appy.pk/docs-lms.php" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="btn btn-primary rounded-pill px-4 py-2 d-flex align-items-center gap-2 shadow-sm"
        >
          <i className="bi bi-book"></i> Read Documentation
        </a>
      </div>

      {/* Iconic Stats Grid */}
      <div className="stats-grid mb-5">
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-secondary mb-1  small fw-bold">Total {studentsTerm}</p>
            <h3 className="mb-0 text-dark">{stats.students?.total || 0}</h3>
          </div>
          <div className="stat-icon bg-primary text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #4361ee 0%, #3a57e8 100%)' }}>
            <i className="bi bi-people-fill"></i>
          </div>
        </div>

        {!isStaff ? (
          <>
            <div className="stat-card">
              <div className="stat-info">
                <p className="text-secondary mb-1  small fw-bold">Today's Collection</p>
                <h3 className="mb-0 text-dark">{getCurrencySymbol(settings?.currency)}{stats.finance?.today_collection || 0}</h3>
              </div>
              <div className="stat-icon bg-warning text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #ff9f43 0%, #ff9030 100%)' }}>
                <i className="bi bi-wallet2"></i>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-info">
                <p className="text-secondary mb-1  small fw-bold">New Enquiries</p>
                <h3 className="mb-0 text-dark">{stats.enquiries?.new_today || 0}</h3>
              </div>
              <div className="stat-icon bg-info text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #00cfe8 0%, #00b9d1 100%)' }}>
                <i className="bi bi-chat-square-quote-fill"></i>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-info">
                <p className="text-secondary mb-1  small fw-bold">Pending Fees</p>
                <h3 className="mb-0 text-dark">{stats.alerts?.pending_fees || 0}</h3>
              </div>
              <div className="stat-icon bg-danger text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #ea5455 0%, #d63334 100%)' }}>
                <i className="bi bi-exclamation-circle-fill"></i>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="stat-card">
              <div className="stat-info">
                <p className="text-secondary mb-1 small fw-bold">{studentsTerm} Attendance</p>
                <h3 className="mb-0 text-dark">{stats.attendance?.students_present || 0} / {stats.attendance?.students_total || 0}</h3>
              </div>
              <div className="stat-icon bg-success text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #28c76f 0%, #17a34a 100%)' }}>
                <i className="bi bi-calendar-check"></i>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-info">
                <p className="text-secondary mb-1 small fw-bold">Staff Attendance</p>
                <h3 className="mb-0 text-dark">{stats.attendance?.staff_present || 0} / {stats.attendance?.staff_total || 0}</h3>
              </div>
              <div className="stat-icon bg-info text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #00cfe8 0%, #00b9d1 100%)' }}>
                <i className="bi bi-person-check"></i>
              </div>
            </div>
             <div className="stat-card">
              <div className="stat-info">
                <p className="text-secondary mb-1  small fw-bold">Active {studentsTerm}</p>
                <h3 className="mb-0 text-dark">{stats.students?.active || 0}</h3>
              </div>
              <div className="stat-icon bg-warning text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #ff9f43 0%, #ff9030 100%)' }}>
                <i className="bi bi-person-video3"></i>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Charts Section */}
      <div className="row g-4 mb-4">
        {!isStaff && (
          <div className="col-lg-8">
            <div className="card-box h-100 shadow-sm">
              <div className="section-header">
                <h5 className="fw-bold text-dark">Revenue Trends</h5>
              </div>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueData}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4361ee" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#4361ee" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e5f2" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#a3aed0', fontSize: 12 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#a3aed0', fontSize: 12 }} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }} />
                    <Area type="monotone" dataKey="amount" stroke="#4361ee" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
        <div className={isStaff ? "col-12" : "col-lg-4"}>
          <div className="card-box h-100">
            <div className="section-header">
              <h5>{studentTerm} Status</h5>
            </div>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={studentStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {studentStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Content Row */}
      <div className="row g-4">
        {/* Recent Students */}
        <div className="col-12">
          <div className="card-box">
            <div className="section-header">
              <h5>Recent Admissions</h5>
              <button className="btn btn-sm btn-outline-primary rounded-pill px-3" onClick={() => navigate('/students')}>View All</button>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="border-0 rounded-start">Name</th>
                    <th className="border-0">{courseTerm}</th>
                    <th className="border-0">{batchTerm}</th>
                    <th className="border-0 rounded-end">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(recentStudents) && recentStudents.length > 0 ? (
                    recentStudents.map(student => (
                      <tr key={student.id}>
                        <td>
                          <div className="d-flex align-items-center gap-3">
                            <div className="avatar-sm bg-purple-light text-primary d-flex align-items-center justify-content-center rounded-circle fw-bold" style={{ width: 40, height: 40 }}>
                              {student.first_name?.charAt(0)}
                            </div>
                            <div>
                              <div className="fw-semibold text-dark">{student.first_name} {student.last_name}</div>
                              <small className="text-muted">ID: #{student.id}</small>
                            </div>
                          </div>
                        </td>
                        <td><span className="badge bg-light text-dark fw-normal border">{student.course?.name}</span></td>
                        <td>{student.batch?.name || `No ${batchTerm}`}</td>
                        <td>
                          <span className={`status-badge ${student.status === 'active' ? 'status-ready' : 'status-cutting'}`}>
                            {student.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-4 text-muted">
                        No recent admissions found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
